import type React from 'react'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import { Platform } from 'react-native'

import { createContextScope } from '@radix-ui/react-context'

import { useTheme } from '~/hooks'

import * as S from './input-select.styles'

import { SelectDropdownList } from './components/input-select-dropdownlist'
import { SelectField } from './components/input-select-field'
import {
  HELPER_TEXT_HEIGHT,
  SelectHelperText,
} from './components/input-select-helpertext'
import { SelectItem } from './components/input-select-item'
import { SelectLabel } from './components/input-select-label'
import { SelectPortal } from './components/input-select-portal'
import { SelectTrigger } from './components/input-select-trigger'
import { SelectValue } from './components/input-select-value'

import type {
  InputSelectContext,
  InputSelectProps,
  InputSelectTriggerLayoutInfo,
  ScopedProps,
} from './input-select.types'

import { useSplitChildren } from './hooks/use-split-children'

/*
----------------------------------------------------------------
Input Select
----------------------------------------------------------------
*/

type SelectElementRef = React.ElementRef<typeof S.Root>

const DISPLAY_NAME = 'InputSelect'

const InputSelectRoot = forwardRef<SelectElementRef, InputSelectProps>(
  (props, ref) => {
    const theme = useTheme()
    const {
      children,
      size = 'ml',
      onSelectionChange,
      defaultSelectedValue,
      onClearSelection,
      orientation = 'bottom',
      maxDropdownHeight = 320,
      testID,
      __scopeSelect,
      ...rest
    } = props as ScopedProps<InputSelectProps>

    const { isDisabled, isReadOnly } = rest

    const [selectedValue, setSelectedValue] = useState<string | null>(null)
    const [isFocused, setIsFocused] = useState(false)
    const [triggerLayoutInfo, setTriggerLayoutInfo] =
      useState<InputSelectTriggerLayoutInfo>({
        width: 0,
        height: 0,
        x: 0,
        y: 0,
      })

    const { dropdownElement, otherElements } = useSplitChildren(children)

    const isClearButtonVisible =
      isFocused && !!selectedValue && !isDisabled && !isReadOnly

    const isIOS = Platform.OS === 'ios'
    const clearButtonTouchableAreaStyles = {
      right: triggerLayoutInfo.x + theme.spacingInlineTenpulse,
      top: isIOS
        ? triggerLayoutInfo.y - triggerLayoutInfo.height
        : triggerLayoutInfo.y - triggerLayoutInfo.height - HELPER_TEXT_HEIGHT,
    }

    const setTriggerToggle = useCallback((type: 'open' | 'close') => {
      setIsFocused(type === 'open')
    }, [])

    const onSelectedValue = useCallback(
      (value: string | null) => {
        setSelectedValue(value)
        if (value) {
          onSelectionChange?.(value)
        }
      },
      [onSelectionChange]
    )

    useEffect(() => {
      if (defaultSelectedValue) {
        setSelectedValue(defaultSelectedValue)
      }
    }, [defaultSelectedValue])

    return (
      <SelectProvider
        {...rest}
        scope={__scopeSelect}
        size={size}
        isFocused={isFocused}
        triggerLayoutInfo={triggerLayoutInfo}
        setTriggerLayoutInfo={setTriggerLayoutInfo}
        setTriggerToggle={setTriggerToggle}
        selectedValue={selectedValue}
        onSelectedValue={onSelectedValue}
        isClearButtonVisible={isClearButtonVisible}
        onClearSelection={onClearSelection}
        testID={testID}
        orientation={orientation}
        maxDropdownHeight={maxDropdownHeight}
      >
        <S.Root {...props} ref={ref} collapsable={false}>
          {otherElements}
        </S.Root>

        <SelectPortal
          visible={isFocused}
          onOverlayPress={() => setTriggerToggle('close')}
        >
          {isClearButtonVisible && (
            <S.ClearButtonTouchableArea
              size={size}
              style={clearButtonTouchableAreaStyles}
              onPress={() => {
                onSelectedValue(null)
                if (typeof onClearSelection === 'function') {
                  onClearSelection()
                }
              }}
            />
          )}
          {dropdownElement}
        </SelectPortal>
      </SelectProvider>
    )
  }
)

InputSelectRoot.displayName = DISPLAY_NAME

/*
----------------------------------------------------------------
Scope Definition
----------------------------------------------------------------
*/

const [createSelectContext] = createContextScope(DISPLAY_NAME)

const [SelectProvider, useSelectContext] =
  createSelectContext<InputSelectContext>(DISPLAY_NAME)

export { useSelectContext, DISPLAY_NAME }

export const InputSelect = {
  Root: InputSelectRoot,
  Field: SelectField,
  Label: SelectLabel,
  Trigger: SelectTrigger,
  Value: SelectValue,
  HelperText: SelectHelperText,
  DropdownList: SelectDropdownList,
  Item: SelectItem,
}
